import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { UserMessage } from '@zero/shared/util-models';
import { commonRoutes } from 'constants/web-app-constants';
import { firstValueFrom } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AuthService } from '../authentication/auth.service';
import { WindowService } from '../dom/window.service';
import { UserMessagesHolderService } from '../user-messages/user-messages-holder.service';

interface ZeroWebAppInterface {
  orderCreate: (isinToUse: string) => Promise<boolean>;
  navToHome: () => Promise<boolean>;
  isLoggedIn: () => Promise<boolean>;
  logout: () => void;
  login: (
    user: string,
    password: string,
    redirectAfterLogin: boolean,
    handleMessages: boolean,
  ) => Promise<{ success: boolean; message: string }>;
}

@Injectable({
  providedIn: 'root',
})
export class WebviewSupportService {
  readonly #router = inject(Router);
  readonly #ngZone = inject(NgZone);
  readonly #windowService = inject(WindowService);
  readonly #authService = inject(AuthService);
  readonly #messageService = inject(UserMessagesHolderService);

  public init = () => {
    (this.#windowService.getWindow() as any).zeroWebApp = {
      orderCreate: this.#orderCreateFn,
      navToHome: this.#navToHome,
      isLoggedIn: this.#isUserLoggedIn,
      logout: this.#logout,
      login: this.#login,
    } as ZeroWebAppInterface;
  };

  #orderCreateFn = (isinToUse: string): Promise<boolean> =>
    this.#ngZone.run(() =>
      this.#router.navigate([commonRoutes.postlogin.buyAndSell], {
        queryParams: { isin: isinToUse, direction: 'buy' },
      }),
    );

  #navToHome = (): Promise<boolean> => this.#ngZone.run(() => this.#router.navigate([commonRoutes.postlogin.member_home]));

  #isUserLoggedIn = (): Promise<boolean> => this.#ngZone.run(() => firstValueFrom(this.#authService.isLoggedIn$().pipe(take(1))));

  #logout = (): void => {
    this.#ngZone.run(() =>
      this.#authService.logout(undefined, true, undefined, {
        reason: 'explicit logout through customer',
      }),
    );
  };

  #login = (
    user: string,
    password: string,
    redirectAfterLogin: boolean,
    handleMessages: boolean,
  ): Promise<{ success: boolean; message: string }> =>
    this.#ngZone.run(() => {
      if (handleMessages) {
        this.#messageService.clearAllMessages();
      }
      return firstValueFrom(
        this.#authService.login(user, password).pipe(
          map((res) => {
            if (handleMessages) {
              if (!res.success && res.message) {
                this.#messageService.addMessage(new UserMessage(res.message, 'danger', true));
              } else if (res.message) {
                this.#messageService.addSuccessMessage(res.message);
              }
            }

            if (redirectAfterLogin) {
              this.#authService.redirectAfterLogin();
            }
            return { success: res.success, message: res.message };
          }),
        ),
      );
    });
}
